import type { MetaDescriptor } from "react-router";
import { SITE_URL, toCanonical } from "../components/SEO/applySEO";
import i18n from "../i18n/i18n";
import { splitLocalePath, withLocale, type AppLocale } from "../i18n/localePath";
import { HOME_FAQ_KEYS } from "../UI/pages/Home/components/landing/faqKeys";
import { SCREENER_FAQ_KEYS } from "../UI/pages/StockScreener/screenerSeoLinks";
import { ETF_FAQ_KEYS } from "../UI/pages/EtfScreener/etfSeoLinks";
import { Paths } from "../router/paths";

/**
 * Page-specific schema.org JSON-LD, server-rendered in <head> through the
 * routes' `meta`. The company graph is on the home page only; every other
 * indexable page gets a BreadcrumbList plus its own type where one fits
 * (calculators, FAQs, articles in seo/articleSeo.ts).
 */

type PathKey = keyof typeof Paths;
type Translate = (key: string) => string;
export type JsonLd = Record<string, unknown>;

const CONTEXT = "https://schema.org";

const ORGANIZATION: JsonLd = {
  "@context": CONTEXT,
  "@type": "Organization",
  name: "Akinda",
  alternateName: "Akinda Halal Investment Platform",
  url: SITE_URL,
  logo: `${SITE_URL}/Logomark.webp`,
  description:
    "Akinda is a Halal investment platform offering stock analysis, portfolio management, and insights for ethical investments in financial markets.",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "Customer Service",
    email: "contact@akinda.io",
    availableLanguage: ["English"],
  },
  // The profiles linked in the site footer (LandingFooter.tsx).
  sameAs: [
    "https://www.linkedin.com/company/akindaio/",
    "https://www.instagram.com/akinda.io/",
    "https://t.me/akindaio",
  ],
  foundingDate: "2020",
};

const WEBSITE: JsonLd = {
  "@context": CONTEXT,
  "@type": "WebSite",
  name: "Akinda",
  url: SITE_URL,
  description:
    "Halal investment platform specializing in stock analysis and portfolio management",
};

/** Short page names for breadcrumbs (English, like all server-rendered SEO). */
const CRUMB_NAME: Partial<Record<PathKey, string>> = {
  ABOUT_US: "About",
  ABOUT_FOUNDER: "Founder",
  OUR_MISSION: "Our Mission",
  CONTACT: "Contact",
  COMMUNITY: "Community",
  FOR_BUSINESS: "For Business",
  MCP: "MCP Server",
  STARTUPS: "Startups",
  REFERRAL: "Referral Program",
  PRIVACY_POLICY: "Privacy Policy",
  TERMS_AND_CONDITIONS: "Terms & Conditions",
  DISCLOSURES: "Disclosures",
  SHARIAH_SCREENING_METHODOLOGY: "Shariah Screening Methodology",
  STOCK_SCREENER: "Stock Screener",
  ETF_SCREENER: "ETF Screener",
  STOCK_ANALYSES: "Stock Analyses",
  TOP_HALAL_STOCKS: "Top Halal Stocks",
  SCREENING: "Screening",
  INSIDER_BY_SECTOR: "Insider Trading by Sector",
  DIVIDEND: "Dividends",
  SUMMARY: "Market Summary",
  MARKET_NEWS: "Market News",
  READY_MADE_PORTFOLIO: "Ready-Made Portfolios",
  ARTICLES: "Articles",
  ACADEMY: "Academy",
  WEBINARS: "Webinars",
  INVESTING_CALCULATOR: "Investing Calculator",
  ZAKAT_CALCULATOR: "Zakat Calculator",
  PURIFICATION_CALCULATOR: "Purification Calculator",
  BACKTESTING: "Portfolio Backtesting",
  AKINDA_AI: "Akinda AI",
};

/** Pages that sit under another page in the breadcrumb. */
const CRUMB_PARENT: Partial<Record<PathKey, PathKey>> = {
  ABOUT_FOUNDER: "ABOUT_US",
  READY_MADE_PORTFOLIO_ID: "READY_MADE_PORTFOLIO",
  ARTICLE_DETAIL: "ARTICLES",
  WEBINAR_DETAIL_ID: "WEBINARS",
  COURSE_INFO_ID: "ACADEMY",
  SYMBOL: "STOCK_SCREENER",
  ETF: "ETF_SCREENER",
};

export interface Crumb {
  name: string;
  path: string;
}

/**
 * A page's breadcrumb name in the URL's language (pages.stock.breadcrumbs.*),
 * falling back to the English names above. getFixedT never changes an
 * instance's language, so this is safe during concurrent server renders.
 */
const crumbName = (key: PathKey | "HOME", locale: AppLocale | null | undefined): string => {
  const fallback = key === "HOME" ? "Home" : (CRUMB_NAME[key] ?? "Page");
  return i18n.getFixedT(locale ?? "en")(`pages.stock.breadcrumbs.${key}`, {
    defaultValue: fallback,
  });
};

/** BreadcrumbList starting at Home; the last crumb is the current page. */
export const breadcrumbJsonLd = (trail: Crumb[], locale?: AppLocale | null): JsonLd => ({
  "@context": CONTEXT,
  "@type": "BreadcrumbList",
  itemListElement: [
    { name: crumbName("HOME", locale), path: locale ? withLocale("/", locale) : "/" },
    ...trail,
  ].map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: toCanonical(crumb.path),
  })),
});

/** Home → parent (if any) → this page. `name` overrides the page's own name. */
export const routeBreadcrumb = (key: PathKey, path: string, name?: string): JsonLd => {
  const locale = splitLocalePath(path).locale;
  const localize = (value: string) => (locale ? withLocale(value, locale) : value);
  const parent = CRUMB_PARENT[key];
  const trail: Crumb[] = [];
  if (parent && CRUMB_NAME[parent]) {
    trail.push({ name: crumbName(parent, locale), path: localize(Paths[parent]) });
  }
  trail.push({ name: name ?? crumbName(key, locale), path: localize(path) });
  return breadcrumbJsonLd(trail, locale);
};

const CALCULATORS: Partial<Record<PathKey, { name: string; description: string }>> = {
  ZAKAT_CALCULATOR: {
    name: "Zakat Calculator",
    description: "Estimate the zakat due on cash, gold, silver, stocks and other wealth.",
  },
  PURIFICATION_CALCULATOR: {
    name: "Stock Purification Calculator",
    description:
      "Work out how much of your dividends and gains to give away to purify non-compliant income.",
  },
  INVESTING_CALCULATOR: {
    name: "Investing Calculator",
    description: "Project how a halal investment grows with regular contributions over time.",
  },
};

const webApplication = (key: PathKey, path: string): JsonLd | null => {
  const calculator = CALCULATORS[key];
  if (!calculator) return null;
  return {
    "@context": CONTEXT,
    "@type": "WebApplication",
    name: calculator.name,
    description: calculator.description,
    url: toCanonical(path),
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
};

/** FAQPage from the same translation keys the page shows, so they always match. */
export const faqJsonLd = (items: { q: string; a: string }[]): JsonLd => ({
  "@context": CONTEXT,
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

const pageFaq = (key: PathKey, t: Translate): JsonLd | null => {
  if (key === "HOME") {
    return faqJsonLd(
      HOME_FAQ_KEYS.map((k) => ({ q: t(`home.faq.${k}.q`), a: t(`home.faq.${k}.a`) })),
    );
  }
  if (key === "ETF_SCREENER") {
    const p = "pages.etfScreener.seoContent.faq";
    return faqJsonLd(ETF_FAQ_KEYS.map((k) => ({ q: t(`${p}.${k}.q`), a: t(`${p}.${k}.a`) })));
  }
  if (key === "STOCK_SCREENER") {
    const p = "pages.stockScreener.seoContent.faq";
    return faqJsonLd(
      SCREENER_FAQ_KEYS.map((k) => ({ q: t(`${p}.${k}.q`), a: t(`${p}.${k}.a`) })),
    );
  }
  return null;
};

/**
 * JSON-LD for a page served by the root meta (pages with their own route
 * meta, like stock and article pages, add theirs there). Only indexable
 * pages get any.
 */
export const routeJsonLd = (
  key: PathKey | undefined,
  pathname: string,
  indexable: boolean,
  t: Translate,
): JsonLd[] => {
  if (!key || !indexable) return [];
  if (key === "HOME") return [ORGANIZATION, WEBSITE, pageFaq(key, t)!];
  return [routeBreadcrumb(key, pathname), webApplication(key, pathname), pageFaq(key, t)].filter(
    (value): value is JsonLd => value !== null,
  );
};

export const jsonLdMeta = (values: JsonLd[]): MetaDescriptor[] =>
  values.map((value) => ({ "script:ld+json": value }));
